import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik, FormikProps } from "formik";
import * as yup from "yup";
import { get, isEmpty } from "lodash";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { InputTextAreaField } from "../../../../components/Shared/InputTextAreaField";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import Tooltip from "../../../../components/Common/Display/Tooltip";
import { request } from "../../../../utils/Request";
import { Divider } from "primereact/divider";
import { utcDateToLocalDate } from "../../../../utils/Helper";

interface IProps {
    visible: boolean;
    toastify: any;
    damageCase: any;
    onClose: any;
}

type TInputForm = {
    comments: string;
};

const DEFAULT_FORM_STATE: TInputForm = {
    comments: '',
};

type TCalculationRow = {
    id: string | number;
    title?: string | null;
    status?: string | null;
    original_status?: string | null;
    created_at?: string | null;
    accepted_at?: string | null;
    rejected_at?: string | null;
    download_url?: string | null;
    author_name?: string | null;
    modifier_name?: string | null;
};

const validationSchema = (t: Function): any => {
    return yup.object().shape({
        comments: yup.string().required(t('the field is required.', { field: t('comments') })),
    });
};

function OrderDamageCaseCalculationUpdate(props: IProps) {
    const { toastify, damageCase } = props;
    const { t }: any = useTranslation();
    const formRef: any = useRef<FormikProps<TInputForm>>();
    const [open, setOpen] = useState<boolean>(false);
    const [initFormState, setInitFormState] = useState<TInputForm>(DEFAULT_FORM_STATE);
    const [rows, setRows] = useState<TCalculationRow[]>([]);
    const busyRef = useRef<boolean>(false);

    useEffect(() => {
        if (props.visible && damageCase) {
            setOpen(true);
            setInitFormState(DEFAULT_FORM_STATE);

            const calculations = get(damageCase, ['calculations']) || [];
            const initialRows: TCalculationRow[] = calculations.map((c: any) => ( {
                id: get(c, ['id']),
                title: get(c, ['title']),
                status: get(c, ['status']),
                original_status: get(c, ['status']),
                created_at: get(c, ['created_at']),
                accepted_at: get(c, ['accepted_at']),
                rejected_at: get(c, ['rejected_at']),
                download_url: get(c, ['download_url']),
                author_name: get(c, ['author_name']),
                modifier_name: get(c, ['modifier_name']),
            } ));

            setRows(initialRows);
        } else if (!props.visible) {
            setOpen(false);
            setRows([]);
        }
    }, [props.visible, damageCase]);

    const onClose = () => {
        setOpen(false);
        props.onClose();
    };

    const onReset = () => {
        const base: any = formRef.current;
        if (base) {
            base.resetForm();
        }
        setInitFormState(DEFAULT_FORM_STATE);

        const calculations = get(damageCase, ['calculations']) || [];
        const initialRows: TCalculationRow[] = calculations.map((c: any) => ( {
            id: get(c, ['id']),
            title: get(c, ['title']),
            status: get(c, ['status']),
            original_status: get(c, ['status']),
            created_at: get(c, ['created_at']),
            accepted_at: get(c, ['accepted_at']),
            rejected_at: get(c, ['rejected_at']),
            download_url: get(c, ['download_url']),
            author_name: get(c, ['author_name']),
            modifier_name: get(c, ['modifier_name']),
        } ));
        setRows(initialRows);
    };

    const updateStatus = (id: any, status: 'accepted' | 'rejected') => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    };

    const onSubmit = async (payloads: TInputForm) => {
        if (busyRef.current) return;
        busyRef.current = true;

        const changed = rows
            .filter((r) => r.status && r.original_status && r.status !== r.original_status)
            .filter((r) => r.status === 'accepted' || r.status === 'rejected')
            .map((r) => ( {
                id: r.id,
                calculation_status: r.status,
            } ));

        if (isEmpty(changed)) {
            toastify(t('no changes'), "info");
            busyRef.current = false;
            return;
        }

        try {
            await request({
                method: 'POST',
                url: `/suppliers/provider/order/damage/cases/${ get(damageCase, ['id']) }/update/damage/codes`,
                data: {
                    type: "calculation-status-update",
                    calculation_status: changed,
                    comments: payloads.comments,
                },
            });

            toastify(t("record updated", { item: t('damage case') }), "info");
            onClose();
        } catch (e: any) {
            if (e.status === 422 && e.data && e.data.errors) {
                toastify(t("validation error"), "error");
            } else if (e.status === 404) {
                toastify(t("action not allowed: 404"), "error");
            } else {
                toastify(t("server error"), "error");
            }
        } finally {
            busyRef.current = false;
        }
    };

    const statusTemplate = (row: TCalculationRow) => {
        const status = row.status || '-';
        const severity =
            status === 'accepted' ? 'success' :
            status === 'rejected' ? 'danger' :
            status === 'pending' ? 'warning' :
            'secondary';

        return <Tag value={ status === '-' ? '-' : t(status) } severity={ severity as any } />;
    };

    const dateTemplate = (value?: string | null) => {
        return value ? utcDateToLocalDate(value) : '-';
    };

    const actionTemplate = (row: TCalculationRow) => {
        const status = row.status;

        if (status === 'accepted') {
            return <div>-</div>;
        }

        return (
            <div className="d-flex gap-2 justify-content-end">
                <Button
                    type="button"
                    size="small"
                    severity="success"
                    outlined={ status !== 'accepted' }
                    onClick={ () => updateStatus(row.id, 'accepted') }
                >
                    { t('accept') }
                </Button>
                { status !== 'accepted' && (
                    <Button
                        type="button"
                        size="small"
                        severity="danger"
                        outlined
                        onClick={ () => updateStatus(row.id, 'rejected') }
                    >
                        { t('reject') }
                    </Button>
                ) }
            </div>
        );
    };

    const titleTemplate = (row: TCalculationRow) => {
        const title = row.title || '-';
        const url = row.download_url;

        if (!url) return <div>{ title }</div>;

        return (
            <div className="d-flex align-items-center gap-2">
                <div className="flex-grow-1">{ title }</div>
                <Tooltip tooltip={ t('download') }>
                    <a className="cursor-pointer" href={ url } target="_blank" rel="noreferrer">
                        <i className="fas fa-download" />
                    </a>
                </Tooltip>
            </div>
        );
    };

    return (

        <Sidebar
            visible={ open }
            position="right"
            onHide={ onClose }
            style={ { width: '900px' } }
            header={
                <div className="bg-body-tertiary card-header">
                    <h5 className="card-title mt-0">
                        { get(damageCase, ['case_number']) ? t('update calculations') + ' #' + get(damageCase, ['case_number']) : t('update damage codes') }
                    </h5>
                    <p>{ t("update calculation status") }</p>
                </div>
            }
        >
            <Formik
                innerRef={ formRef }
                initialValues={ initFormState }
                enableReinitialize
                validationSchema={ validationSchema(t) }
                onSubmit={ onSubmit }
            >
                {(formikProps: FormikProps<TInputForm>) => (
                    <Form>
                        <Row className="mb-3">
                            <Col md={ 12 }>
                                <Field
                                    name="comments"
                                    component={ InputTextAreaField }
                                    placeholder={ t('comments') }
                                    label={ t('comments') }
                                />
                            </Col>
                        </Row>

                        <Divider />

                        <div className="d-flex justify-content-end gap-2 mb-3">
                            <Button
                                type="button" size="small"
                                severity="secondary"
                                outlined
                                onClick={ onReset }
                            >
                                { t('reset') }
                            </Button>
                            <Button type="submit"
                                    size="small"
                                    outlined
                                    disabled={ formikProps.isSubmitting }>
                                { t('update') }
                            </Button>
                        </div>

                        <DataTable value={ rows } responsiveLayout="scroll" dataKey="id">
                            <Column header={ t('title') } body={ titleTemplate } />
                            <Column
                                header={ t('created at') }
                                body={ (row: TCalculationRow) => dateTemplate(row.created_at) }
                            />
                            <Column header={ t('status') } body={ statusTemplate } style={{ width: '140px' }} />
                            <Column
                                header={ t('accepted at') }
                                body={ (row: TCalculationRow) => dateTemplate(row.accepted_at) }
                                style={{ width: '160px' }}
                            />
                            <Column
                                header={ t('rejected at') }
                                body={ (row: TCalculationRow) => dateTemplate(row.rejected_at) }
                                style={{ width: '160px' }}
                            />
                            <Column header={ t('action') } body={ actionTemplate } style={{ width: '220px' }} />
                        </DataTable>
                    </Form>
                )}
            </Formik>
        </Sidebar>
    );
}

export default OrderDamageCaseCalculationUpdate;

